import type { GlobalUploadFileEntry } from "@/features/globalUpload/globalUploadConstants";

/** Dateiendung inkl. Punkt, z. B. `.pdf`. Leer, wenn keine Endung erkennbar ist. */
export function getFileExtension(filename: string): string {
  const trimmed = filename.trim();
  const dotIndex = trimmed.lastIndexOf(".");
  if (dotIndex <= 0) return "";
  return trimmed.slice(dotIndex).toLowerCase();
}

export function hasChangedFileExtension(originalName: string, customName: string): boolean {
  const originalExt = getFileExtension(originalName);
  const customExt = getFileExtension(customName.trim());
  if (!originalExt) return false;
  return customExt !== originalExt;
}

export function findFilesWithChangedExtension(
  files: GlobalUploadFileEntry[],
): GlobalUploadFileEntry[] {
  return files.filter((entry) =>
    hasChangedFileExtension(entry.original.name, entry.customName || entry.original.name),
  );
}

export function describeExtensionChange(originalName: string, customName: string): string {
  const originalExt = getFileExtension(originalName);
  const customExt = getFileExtension(customName.trim());
  if (!originalExt) return "";
  if (!customExt) return `Endung ${originalExt} entfernt`;
  if (customExt !== originalExt) return `Endung von ${originalExt} zu ${customExt} geändert`;
  return "";
}

function getOriginalExtensionRaw(originalName: string): string {
  const trimmed = originalName.trim();
  const dotIndex = trimmed.lastIndexOf(".");
  if (dotIndex <= 0) return "";
  return trimmed.slice(dotIndex);
}

/** Dateiname aus Vorlage: Originalendung bleibt, `{{Dokumentenname}}` wird der Originalname. */
export function applyTemplateToFileName(templateValue: string, originalName: string): string {
  const originalDotIndex = originalName.lastIndexOf(".");
  const originalExtension = originalDotIndex >= 0 ? originalName.slice(originalDotIndex) : "";
  const originalBase =
    originalDotIndex >= 0 ? originalName.slice(0, originalDotIndex) : originalName;
  const templateDotIndex = templateValue.lastIndexOf(".");
  const templateBase =
    templateDotIndex >= 0 ? templateValue.slice(0, templateDotIndex) : templateValue;
  const withDocumentName = templateBase.replaceAll("{{Dokumentenname}}", originalBase);
  return `${withDocumentName}${originalExtension}`;
}

/** Stellt die Original-Endung wieder her (fehlend ergänzen, falsche ersetzen). */
export function suggestCorrectedFileName(originalName: string, customName: string): string {
  const originalExtRaw = getOriginalExtensionRaw(originalName);
  if (!originalExtRaw) return customName.trim();

  const custom = customName.trim();
  const customDotIndex = custom.lastIndexOf(".");
  const customBase =
    customDotIndex > 0 ? custom.slice(0, customDotIndex) : custom;

  return `${customBase}${originalExtRaw}`;
}

export function applyExtensionCorrections(
  files: GlobalUploadFileEntry[],
): GlobalUploadFileEntry[] {
  return files.map((entry) => {
    const customName = entry.customName || entry.original.name;
    if (!hasChangedFileExtension(entry.original.name, customName)) return entry;
    return {
      ...entry,
      customName: suggestCorrectedFileName(entry.original.name, customName),
      hasConflict: false,
    };
  });
}
